/**
 * ZMetrics - Zod Validation Schemas for Metrics API
 *
 * Single source of truth for Observability Dashboard API validation.
 * These schemas generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * Admin-panel endpoints only - protected by AdminPermissionGuard (PBAC).
 * Base path: /api/admin/metrics
 */

import { z } from "zod";

import { LLM_FAILURE_CLASSES, LLM_FAILURE_SCOPES } from "../../../shared/llm-failure";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS - Reusable pieces
// ═══════════════════════════════════════════════════════════════

/**
 * Time range enum for dashboard queries
 */
export const TimeRangeSchema = z.enum(["1h", "6h", "24h", "7d", "30d"]);
export type TimeRange = z.infer<typeof TimeRangeSchema>;

/**
 * Metric type enum (matches Prisma MetricType)
 */
export const MetricTypeSchema = z.enum(["COUNTER", "GAUGE", "HISTOGRAM"]);
export type MetricType = z.infer<typeof MetricTypeSchema>;

/**
 * Common query params for time-based metrics
 */
const TimeRangeParamsSchema = z.object({
  range: TimeRangeSchema.optional().default("24h"),
  organizationId: z.string().min(1).optional()
});

/**
 * Granularity for time series aggregation
 */
export const GranularitySchema = z.enum(["1m", "5m", "15m", "1h", "1d"]);
export type Granularity = z.infer<typeof GranularitySchema>;

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS - Input validation
// ═══════════════════════════════════════════════════════════════

/**
 * OVERVIEW - Query parameters
 */
export const OverviewParamsSchema = z.object({
  organizationId: z.string().min(1).optional()
});

/**
 * TIME SERIES - Query parameters for historical data
 */
export const TimeSeriesParamsSchema = z.object({
  name: z.string().min(1, "Metric name is required"),
  range: TimeRangeSchema.optional().default("24h"),
  granularity: GranularitySchema.optional().default("5m"),
  labels: z.record(z.string(), z.string()).optional(),
  organizationId: z.string().min(1).optional()
});

/**
 * HTTP METRICS - Query parameters
 */
export const HttpMetricsParamsSchema = TimeRangeParamsSchema.extend({
  path: z.string().optional(),
  method: z.enum(["GET", "POST", "PUT", "DELETE", "PATCH"]).optional(),
  statusCode: z.coerce.number().int().min(100).max(599).optional()
});

/**
 * AI USAGE - Query parameters
 */
export const AiUsageParamsSchema = TimeRangeParamsSchema.extend({
  provider: z.enum(["openai", "anthropic", "google", "mistral", "lambda"]).optional(),
  model: z.string().optional()
});

/**
 * QUEUE STATS - Query parameters
 */
export const QueueStatsParamsSchema = z.object({
  queue: z.string().optional()
});

/**
 * WORKFLOW STATS - Query parameters
 */
export const WorkflowStatsParamsSchema = TimeRangeParamsSchema.extend({
  status: z.enum(["success", "failed", "cancelled"]).optional(),
  triggerType: z.enum(["test", "agent_input", "webhook", "schedule", "subworkflow"]).optional()
});

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS - Output validation
// ═══════════════════════════════════════════════════════════════

/**
 * Single data point for time series charts
 */
export const DataPointSchema = z.object({
  timestamp: z.string().datetime(),
  value: z.number()
});
export type DataPoint = z.infer<typeof DataPointSchema>;

/**
 * Sparkline data (simplified time series for small charts)
 */
export const SparklineDataSchema = z.array(z.number());
export type SparklineData = z.infer<typeof SparklineDataSchema>;

/**
 * Echo (audit logging) status surfaced in the observability dashboard.
 *
 * Four possible states, matching the advisory contract used across all
 * health surfaces — Echo is deliberately isolated from the Nexus critical
 * path, so this status is observability-only, never gating.
 *
 *   - disabled   : Echo not configured (dev/local / ECHO_ENABLED=false)
 *   - healthy    : enabled + connected + DB responding
 *   - unreachable: enabled but startup-connect failed, OR runtime query
 *                  failed after a previously successful connect
 */
export const EchoHealthSchema = z.object({
  enabled: z.boolean(),
  available: z.boolean(),
  status: z.enum(["disabled", "healthy", "unreachable"])
});

/**
 * System health status
 */
export const SystemHealthSchema = z.object({
  cpu: z.number().min(0).max(100),
  memory: z.object({
    used: z.number(),
    total: z.number(),
    percentage: z.number().min(0).max(100)
  }),
  uptime: z.number(),
  echo: EchoHealthSchema
});

/**
 * Overview stat card data
 */
export const StatCardSchema = z.object({
  label: z.string(),
  value: z.union([z.number(), z.string()]),
  unit: z.string().optional(),
  change: z.number().optional(),
  changeLabel: z.string().optional(),
  sparkline: SparklineDataSchema.optional()
});
export type StatCard = z.infer<typeof StatCardSchema>;

/**
 * OVERVIEW - Response
 */
export const OverviewResponseSchema = z.object({
  system: SystemHealthSchema,
  stats: z.object({
    requestsPerSecond: StatCardSchema,
    errorRate: StatCardSchema,
    activeWorkflows: StatCardSchema,
    aiTokensUsed: StatCardSchema
  }),
  timestamp: z.string().datetime()
});
export type OverviewResponse = z.infer<typeof OverviewResponseSchema>;

/**
 * TIME SERIES - Response
 */
export const TimeSeriesResponseSchema = z.object({
  name: z.string(),
  type: MetricTypeSchema,
  labels: z.record(z.string(), z.string()).optional(),
  data: z.array(DataPointSchema),
  aggregation: z.object({
    min: z.number(),
    max: z.number(),
    avg: z.number(),
    sum: z.number(),
    count: z.number()
  })
});
export type TimeSeriesResponse = z.infer<typeof TimeSeriesResponseSchema>;

/**
 * HTTP request stats
 */
export const HttpRequestStatsSchema = z.object({
  total: z.number(),
  byStatus: z.array(
    z.object({
      status: z.number(),
      count: z.number(),
      percentage: z.number()
    })
  ),
  byMethod: z.array(
    z.object({
      method: z.string(),
      count: z.number(),
      percentage: z.number()
    })
  ),
  topEndpoints: z.array(
    z.object({
      path: z.string(),
      method: z.string(),
      count: z.number(),
      avgDuration: z.number(),
      errorRate: z.number()
    })
  ),
  latencyPercentiles: z.object({
    p50: z.number(),
    p90: z.number(),
    p95: z.number(),
    p99: z.number()
  }),
  timeSeries: z.array(DataPointSchema)
});

/**
 * HTTP METRICS - Response
 */
export const HttpMetricsResponseSchema = z.object({
  range: TimeRangeSchema,
  stats: HttpRequestStatsSchema,
  timestamp: z.string().datetime()
});
export type HttpMetricsResponse = z.infer<typeof HttpMetricsResponseSchema>;

/**
 * AI provider usage stats
 */
export const AiProviderStatsSchema = z.object({
  provider: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  requestCount: z.number(),
  estimatedCost: z.number(),
  byModel: z.array(
    z.object({
      model: z.string(),
      inputTokens: z.number(),
      outputTokens: z.number(),
      requestCount: z.number(),
      avgLatency: z.number()
    })
  )
});

/**
 * AI USAGE - Response
 */
export const AiUsageResponseSchema = z.object({
  range: TimeRangeSchema,
  totalTokens: z.number(),
  totalCost: z.number(),
  byProvider: z.array(AiProviderStatsSchema),
  timeSeries: z.object({
    tokens: z.array(DataPointSchema),
    prompt: z.array(DataPointSchema).optional(),
    completion: z.array(DataPointSchema).optional(),
    requests: z.array(DataPointSchema)
  }),
  timestamp: z.string().datetime()
});
export type AiUsageResponse = z.infer<typeof AiUsageResponseSchema>;

/**
 * Queue stats for a single queue.
 *
 * A discriminated union rather than counts-plus-an-error-flag, because the
 * dashboard's failure mode is reporting a healthy zero for something it did not
 * actually read. When `reachable` is false there are no counts to render: the
 * type makes "0 waiting, 0 failed, not paused" unrepresentable for a queue
 * whose Redis round-trip failed.
 */
export const QueueInfoSchema = z.discriminatedUnion("reachable", [
  z.object({
    reachable: z.literal(true),
    name: z.string(),
    waiting: z.number(),
    active: z.number(),
    completed: z.number(),
    failed: z.number(),
    delayed: z.number(),
    paused: z.boolean()
  }),
  z.object({
    reachable: z.literal(false),
    name: z.string(),
    error: z.string()
  })
]);
export type QueueInfo = z.infer<typeof QueueInfoSchema>;

/**
 * QUEUE STATS - Response
 *
 * `totals` sums the reachable queues only; `unreachable` is how many were left
 * out, so a partial read can never be presented as a complete one.
 */
export const QueueStatsResponseSchema = z.object({
  queues: z.array(QueueInfoSchema),
  totals: z.object({
    waiting: z.number(),
    active: z.number(),
    completed: z.number(),
    failed: z.number(),
    unreachable: z.number()
  }),
  timestamp: z.string().datetime()
});
export type QueueStatsResponse = z.infer<typeof QueueStatsResponseSchema>;

/**
 * Workflow execution stats
 */
export const WorkflowExecutionStatsSchema = z.object({
  total: z.number(),
  byStatus: z.array(
    z.object({
      status: z.string(),
      count: z.number(),
      percentage: z.number()
    })
  ),
  byTrigger: z.array(
    z.object({
      trigger: z.string(),
      count: z.number(),
      percentage: z.number()
    })
  ),
  avgDuration: z.number(),
  successRate: z.number(),
  activeCount: z.number()
});

/**
 * WORKFLOW STATS - Response
 */
export const WorkflowStatsResponseSchema = z.object({
  range: TimeRangeSchema,
  stats: WorkflowExecutionStatsSchema,
  timeSeries: z.object({
    executions: z.array(DataPointSchema),
    duration: z.array(DataPointSchema)
  }),
  topWorkflows: z.array(
    z.object({
      workflowId: z.string(),
      name: z.string(),
      executionCount: z.number(),
      successRate: z.number(),
      avgDuration: z.number()
    })
  ),
  timestamp: z.string().datetime()
});
export type WorkflowStatsResponse = z.infer<typeof WorkflowStatsResponseSchema>;

/**
 * Redis stats - Memory, hit/miss rate, clients
 */
export const RedisStatsResponseSchema = z.object({
  memory: z.object({
    used: z.number(),
    usedHuman: z.string(),
    peak: z.number(),
    peakHuman: z.string(),
    fragmentation: z.number(),
    maxmemory: z.number(),
    maxmemoryHuman: z.string(),
    isUnlimited: z.boolean()
  }),
  clients: z.object({
    connected: z.number(),
    blocked: z.number()
  }),
  stats: z.object({
    totalKeys: z.number(),
    totalCommands: z.number(),
    instantaneousOps: z.number(),
    keyspaceHits: z.number(),
    keyspaceMisses: z.number(),
    hitRate: z.number(),
    pureCacheHits: z.number(),
    pureCacheMisses: z.number(),
    pureCacheHitRate: z.number()
  }),
  rateLimiter: z.object({
    totalQueues: z.number(),
    totalQueuedNodes: z.number(),
    topQueues: z.array(
      z.object({
        organizationId: z.string(),
        queueDepth: z.number(),
        timestampsCount: z.number()
      })
    )
  })
});
export type RedisStatsResponse = z.infer<typeof RedisStatsResponseSchema>;

/**
 * External API rate limit info for a single API.
 *
 * 🔴 `limit`, `remaining` AND `utilization` ARE PRESENT ONLY WHEN A PROBE
 * MEASURED THEM, AND THEIR ABSENCE IS THE "NOT MEASURED" STATE. A probe that
 * cannot see a provider's limits — Groq's liveness check, which sends no
 * billable request and so gets no per-model rate-limit headers; Google's SDK,
 * which exposes none; any unhealthy or unconfigured row — omits all three. It
 * used to write `0` into them instead, and `0` is a MEASUREMENT: it rendered as
 * "0% used" and "0 left" beside a green dot over a provider nobody had metered.
 * An absent number cannot be rendered as a reading; a zero always is.
 *
 * `utilization` alone may appear without the other two: a Google row whose
 * failure classifies as `RATE_LIMITED` reports saturation (100) with no limit
 * to put it against.
 */
export const ApiLimitInfoSchema = z.object({
  limit: z.number().optional(),
  remaining: z.number().optional(),
  reset: z.string().optional(),
  utilization: z.number().min(0).max(100).optional(),
  healthy: z.boolean(),
  error: z.string().optional(),
  /**
   * WHY a probe is unhealthy — the same `(class, scope)` verdict every other
   * LLM call site classifies with, so a monitor row and a Sentry tag can never
   * disagree about the same provider error.
   *
   * 🔴 `healthy: false` ALONE CANNOT TELL A DEAD CREDENTIAL FROM A STALE
   * REQUEST, AND CONFLATING THEM IS WHAT TEACHES OPERATORS TO IGNORE THE
   * MONITOR. Only `AUTH_INVALID` indicts the key. `REQUEST_INVALID` means the
   * provider AUTHENTICATED us and refused what we asked for — a retired model
   * id, a malformed payload — so the credential is fine and the probe is what
   * needs fixing.
   *
   * Absent on a healthy row, and absent when the failure matched no predicate:
   * `classifyLlmFailure` returns null rather than guessing, and a guess here
   * would hand an unknown failure a policy. Absent, too, when no provider was
   * asked at all (no key configured) — there is no provider verdict to report.
   */
  failure: z
    .object({
      failureClass: z.enum(LLM_FAILURE_CLASSES),
      scope: z.enum(LLM_FAILURE_SCOPES)
    })
    .optional(),
  /**
   * Present only on a row for a provider the probe NEVER ASKED, because no
   * credential is configured for it. Absent on every other row, healthy and
   * unhealthy alike.
   *
   * 🔴 IT EXISTS BECAUSE AN ABSENT `failure` CANNOT CARRY THIS, AND READING IT AS
   * IF IT COULD ANNOUNCED A CONFIGURATION FACT AS AN OUTAGE. Two different rows
   * carry no verdict: one where a request was sent and no predicate scored the
   * failure, and one where no request was sent at all. The first is a gap in
   * `classifyLlmFailure`; the second is an unset environment variable, and they
   * want opposite messages. `healthy: false` plus `failure: undefined` is
   * byte-identical across both, so the never-asked row has to say so itself —
   * which is what this field is (NEX-4791).
   *
   * ⚠️ `z.literal(true)`, NOT `z.boolean()`, ON PURPOSE. An optional boolean has
   * three states and two of them mean "not this case", so every reader has to
   * decide whether `false` and `undefined` differ. They do not, and there is no
   * `unconfigured: false` to get wrong: present is the only signal.
   *
   * ⚠️ NEVER DERIVE IT FROM THE `error` SENTENCE. `error.includes("not
   * configured")` is a test on prose a human wrote for a human, and it passes for
   * the wrong reason the moment any provider's own message happens to contain
   * those words.
   */
  unconfigured: z.literal(true).optional()
});
export type ApiLimitInfo = z.infer<typeof ApiLimitInfoSchema>;

/**
 * EXTERNAL API LIMITS - Response
 */
export const ExternalApiLimitsResponseSchema = z.object({
  apis: z.record(z.string(), ApiLimitInfoSchema),
  timestamp: z.string().datetime()
});
export type ExternalApiLimitsResponse = z.infer<typeof ExternalApiLimitsResponseSchema>;

// ═══════════════════════════════════════════════════════════════
// ZMETRICS MANIFEST - Export all schemas for runtime use
// ═══════════════════════════════════════════════════════════════

/**
 * ZMetrics - Zod API validation schemas for Observability Dashboard
 *
 * Single source of truth for Admin Metrics API:
 * - Runtime validation (backend + frontend)
 * - Type generation (TApi auto-inferred via InferSchemas)
 *
 * All endpoints are admin-panel only (AdminPermissionGuard + @RequireAdminPermission)
 */
export const ZMetrics = {
  /**
   * Get real-time dashboard overview
   * Returns system health, key stats with sparklines
   */
  Overview: {
    method: "GET" as const,
    path: "/api/admin/metrics/overview",
    Params: OverviewParamsSchema,
    Response: ApiSuccessSchema(OverviewResponseSchema)
  },

  /**
   * Get historical time series data for any metric
   * Used for building custom charts
   */
  TimeSeries: {
    method: "GET" as const,
    path: "/api/admin/metrics/timeseries",
    Params: TimeSeriesParamsSchema,
    Response: ApiSuccessSchema(TimeSeriesResponseSchema)
  },

  /**
   * Get HTTP request metrics
   * Request counts, latency percentiles, top endpoints
   */
  Http: {
    method: "GET" as const,
    path: "/api/admin/metrics/http",
    Params: HttpMetricsParamsSchema,
    Response: ApiSuccessSchema(HttpMetricsResponseSchema)
  },

  /**
   * Get AI token usage stats
   * By provider, model, with cost estimates
   */
  AiUsage: {
    method: "GET" as const,
    path: "/api/admin/metrics/ai",
    Params: AiUsageParamsSchema,
    Response: ApiSuccessSchema(AiUsageResponseSchema)
  },

  /**
   * Get BullMQ queue statistics
   * Queue depths, job counts by state
   */
  Queues: {
    method: "GET" as const,
    path: "/api/admin/metrics/queues",
    Params: QueueStatsParamsSchema,
    Response: ApiSuccessSchema(QueueStatsResponseSchema)
  },

  /**
   * Get workflow execution statistics
   * By status, trigger type, with success rates
   */
  Workflows: {
    method: "GET" as const,
    path: "/api/admin/metrics/workflows",
    Params: WorkflowStatsParamsSchema,
    Response: ApiSuccessSchema(WorkflowStatsResponseSchema)
  },

  /**
   * Get external API rate limit status
   * Track OpenAI, Anthropic, Stripe API usage vs limits
   */
  ExternalApis: {
    method: "GET" as const,
    path: "/api/admin/metrics/external-apis",
    Params: z.object({}),
    Response: ApiSuccessSchema(ExternalApiLimitsResponseSchema)
  },

  /**
   * Get Redis statistics
   * Memory usage, cache hit/miss rate, connected clients
   */
  Redis: {
    method: "GET" as const,
    path: "/api/admin/metrics/redis",
    Params: z.object({}),
    Response: ApiSuccessSchema(RedisStatsResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// CONVENIENCE TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════

export type OverviewParams = z.infer<typeof OverviewParamsSchema>;
export type TimeSeriesParams = z.infer<typeof TimeSeriesParamsSchema>;
export type HttpMetricsParams = z.infer<typeof HttpMetricsParamsSchema>;
export type AiUsageParams = z.infer<typeof AiUsageParamsSchema>;
export type QueueStatsParams = z.infer<typeof QueueStatsParamsSchema>;
export type WorkflowStatsParams = z.infer<typeof WorkflowStatsParamsSchema>;
